import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Home,
  Package,
  CreditCard,
  MapPin,
  MessageCircle,
  Building2,
  Receipt,
  Gift,
  Star,
  User,
  LayoutDashboard,
  Upload,
  Users,
  Banknote,
  Tag,
  Shield,
  Megaphone,
  MessageSquare,
  BarChart2,
  FileText,
  LogOut,
  Menu,
  X,
  RefreshCw,
  UserRoundSearch,
  UserPlus,
} from "lucide-react";
import { useAuth } from "../features/auth/context/AuthContext";
import ThemeToggle from "./components/ThemeToggle";
import d2dLogo from "../assets/brand/d2d-logo.png";

const publicLinks = [
  { label: "Services", href: "#services" },
  { label: "Track", href: "#tracking" },
  { label: "Promotions", href: "#promotions" },
];

const CUSTOMER_NAV = [
  { icon: Home, label: "Home", to: "/home" },
  { icon: Package, label: "My Items", to: "/items" },
  { icon: CreditCard, label: "Pay Now", to: "/pay" },
  { icon: MapPin, label: "Track", to: "/track" },
  { icon: MessageCircle, label: "Chat", to: "/chat" },
  { icon: Building2, label: "Addresses", to: "/addresses" },
  { icon: Receipt, label: "Receipts", to: "/receipts" },
  { icon: Gift, label: "Refer & Earn", to: "/refer" },
  { icon: Star, label: "Rewards", to: "/loyalty" },
  { icon: User, label: "Profile", to: "/profile" },
];

const CUSTOMER_BOTTOM = CUSTOMER_NAV.slice(0, 5);

const ADMIN_NAV = [
  { icon: LayoutDashboard, label: "Dashboard", to: "/admin" },
  { icon: Upload, label: "Upload", to: "/admin/upload" },
  { icon: Users, label: "Users", to: "/admin/users" },
  { icon: Banknote, label: "Transactions", to: "/admin/transactions" },
  { icon: Tag, label: "Pricing", to: "/admin/pricing" },
  { icon: Building2, label: "Addresses", to: "/admin/addresses" },
  { icon: Shield, label: "Integrations", to: "/admin/integrations" },
  { icon: Megaphone, label: "Promotions", to: "/admin/promotions" },
  { icon: MessageSquare, label: "Chats", to: "/admin/chats" },
  { icon: BarChart2, label: "Analytics", to: "/admin/analytics" },
  { icon: FileText, label: "Batches", to: "/admin/batches" },
  { icon: RefreshCw, label: "Updates", to: "/admin/updates" },
  { icon: UserRoundSearch, label: "Unmatched", to: "/admin/unmatched" },
];

const ADMIN_BOTTOM = [
  ADMIN_NAV[0],
  { icon: UserPlus, label: "Create User", to: "/admin/users?create=1" },
  ADMIN_NAV[3],
  ADMIN_NAV[6],
  ADMIN_NAV[7],
];

function isActive(pathname, to) {
  const baseTo = to.split("?")[0];
  if (baseTo === "/admin" || baseTo === "/home") return pathname === baseTo;
  return pathname.startsWith(baseTo);
}

/* ── Bottom nav ─────────────────────────────────────────────── */
function BottomNav({ items }) {
  const { pathname } = useLocation();
  return (
    <nav className="bottom-nav">
      {items.map(({ icon: Icon, label, to }) => {
        const active = isActive(pathname, to);
        return (
          <Link
            key={to}
            to={to}
            className={`bottom-nav-item${active ? " active" : ""}`}
          >
            <Icon
              size={22}
              strokeWidth={active ? 2.2 : 1.8}
              className="bottom-nav-icon"
            />
            <span className="bottom-nav-label">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/* ── Sidebar ─────────────────────────────────────────────────── */
function Sidebar({ user, navItems, onLogout }) {
  const { pathname } = useLocation();
  const isAdmin = user?.role === "admin";

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img
          className="sidebar-brand-logo"
          src={d2dLogo}
          alt="D2D Shipping & Logistics Ltd"
        />
        <div className="sidebar-brand-text">
          <small>{isAdmin ? "Admin Panel" : "My Account"}</small>
        </div>
      </div>

      <div className="sidebar-user">
        <div className="sidebar-user-avatar">
          {user?.full_name?.charAt(0)?.toUpperCase() || "?"}
        </div>
        <div className="sidebar-user-info">
          <p className="sidebar-user-name">{user?.full_name?.split(" ")[0]}</p>
          <p className="sidebar-user-mark">
            {isAdmin ? "Administrator" : user?.shipping_mark}
          </p>
        </div>
      </div>

      <nav className="sidebar-nav">
        {navItems.map(({ icon: Icon, label, to }) => {
          const active = isActive(pathname, to);
          return (
            <Link
              key={to}
              to={to}
              className={`sidebar-nav-item${active ? " active" : ""}`}
            >
              <Icon
                size={17}
                strokeWidth={active ? 2.2 : 1.8}
                className="sidebar-nav-icon"
              />
              <span className="sidebar-nav-label">{label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <ThemeToggle />
        <button type="button" className="sidebar-signout" onClick={onLogout}>
          <LogOut size={16} strokeWidth={1.8} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}

/* ── Public nav ──────────────────────────────────────────────── */
function DesktopPublicNav({ user, onLogout }) {
  const dashboardPath =
    user?.role === "admin"
      ? "/admin"
      : user?.onboarding_completed
        ? "/home"
        : "/welcome";
  return (
    <header className="desktop-public-nav">
      <Link to="/" className="desktop-brand">
        <img
          className="public-brand-logo"
          src={d2dLogo}
          alt="D2D Shipping & Logistics Ltd"
        />
      </Link>
      <nav className="desktop-public-nav-links">
        {publicLinks.map((link) => (
          <a key={link.label} href={link.href}>
            {link.label}
          </a>
        ))}
      </nav>
      <div className="desktop-public-actions">
        <ThemeToggle />
        {user ? (
          <>
            <Link
              to={dashboardPath}
              className="desktop-nav-button desktop-nav-button--ghost"
            >
              Dashboard
            </Link>
            <button
              type="button"
              className="desktop-nav-button"
              onClick={onLogout}
            >
              Sign Out
            </button>
          </>
        ) : (
          <>
            <Link
              to="/login"
              className="desktop-nav-button desktop-nav-button--ghost"
            >
              Sign In
            </Link>
            <Link to="/register" className="desktop-nav-button">
              Create Account
            </Link>
          </>
        )}
      </div>
    </header>
  );
}

function MobilePublicNav({ user, onLogout, open, onToggle, onClose }) {
  const dashboardPath =
    user?.role === "admin"
      ? "/admin"
      : user?.onboarding_completed
        ? "/home"
        : "/welcome";

  return (
    <>
      <header className="mobile-public-nav mobile-only-flex">
        <Link to="/" className="mobile-public-brand" onClick={onClose}>
          <img
            className="public-brand-logo public-brand-logo--mobile"
            src={d2dLogo}
            alt="D2D Shipping & Logistics Ltd"
          />
        </Link>

        <button
          type="button"
          className="mobile-public-menu-btn"
          onClick={onToggle}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? (
            <X size={20} strokeWidth={2.2} />
          ) : (
            <Menu size={20} strokeWidth={2.2} />
          )}
        </button>
      </header>

      {open && (
        <>
          <button
            type="button"
            className="mobile-public-overlay mobile-only"
            aria-label="Close menu overlay"
            onClick={onClose}
          />
          <div className="mobile-public-drawer mobile-only-block">
            <nav className="mobile-public-links">
              {publicLinks.map((link) => (
                <a key={link.label} href={link.href} onClick={onClose}>
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="mobile-public-tools">
              <ThemeToggle />
            </div>

            <div className="mobile-public-actions">
              {user ? (
                <>
                  <Link
                    to={dashboardPath}
                    className="btn btn-outline btn-full"
                    onClick={onClose}
                  >
                    Dashboard
                  </Link>
                  <button
                    type="button"
                    className="btn btn-primary btn-full"
                    onClick={() => {
                      onClose();
                      onLogout();
                    }}
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="btn btn-outline btn-full"
                    onClick={onClose}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-primary btn-full"
                    onClick={onClose}
                  >
                    Create Account
                  </Link>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function LandingPromoBar({ user }) {
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    axios
      .get("/api/promotions")
      .then((response) => setPromos(response.data || []))
      .catch(() => {});
  }, []);

  if (!promos.length) return null;

  const primaryPromo = promos[0];
  const extraCount = Math.max(promos.length - 1, 0);

  return (
    <div className="top-promo-bar" id="promotions">
      <div className="top-promo-bar__content">
        <p className="top-promo-bar__eyebrow">Active Offer</p>
        <p className="top-promo-bar__text">
          <strong>{primaryPromo.discount_percent}% off</strong>{" "}
          {primaryPromo.title}
          {extraCount > 0 ? ` + ${extraCount} more live` : ""}
        </p>
      </div>
      {!user ? (
        <div className="top-promo-bar__actions">
          <Link to="/register" className="top-promo-bar__cta">
            Create Account
          </Link>
          <Link to="/login" className="top-promo-bar__link">
            Sign In
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/* ── Shell ───────────────────────────────────────────────────── */
export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isLandingPage = location.pathname === "/";
  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/register";
  const isPublicTrack = location.pathname.startsWith("/track/");
  const isOnboardingPage = location.pathname === "/welcome";
  const showSidebar =
    !!user &&
    !isAuthPage &&
    !isLandingPage &&
    !isPublicTrack &&
    !isOnboardingPage;

  const navItems = user?.role === "admin" ? ADMIN_NAV : CUSTOMER_NAV;
  const bottomItems = user?.role === "admin" ? ADMIN_BOTTOM : CUSTOMER_BOTTOM;
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  if (showSidebar) {
    return (
      <div className="authed-shell">
        <Sidebar user={user} navItems={navItems} onLogout={handleLogout} />
        {/* has-bottom-nav adds padding so content clears the fixed bottom bar */}
        <div className="authed-main has-bottom-nav">
          {children}
          <BottomNav items={bottomItems} />
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell app-shell--public">
      {isLandingPage && <LandingPromoBar user={user} />}
      {isLandingPage && (
        <MobilePublicNav
          user={user}
          onLogout={handleLogout}
          open={mobileMenuOpen}
          onToggle={() => setMobileMenuOpen((previous) => !previous)}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}
      {isLandingPage && (
        <DesktopPublicNav user={user} onLogout={handleLogout} />
      )}
      <main className="app-shell-main">{children}</main>
    </div>
  );
}
